import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import AddEntryModal from '../../components/AddEntryModal';
import Snackbar from '../../components/Snackbar';
import DisciplineSection from './DisciplineSection';
import type { DisciplineConfig } from './DisciplineSection';
import {
    usePersonalRecords,
    useCreateRecord,
    useUpdateRecord,
    useDeleteRecord,
    usePerformanceTests,
    useCreateTest,
    useUpdateTest,
    useDeleteTest,
} from './hooks';
import type { SportDiscipline, PersonalRecord, PerformanceTest } from './hooks';
import type { EntryPayload, RecordInitialValues, TestInitialValues } from '../../components/AddEntryModal';

// ─── Discipline config (pure data, no logic) ──────────────────────────────────

const DISCIPLINES: DisciplineConfig[] = [
    {
        sport: 'running',
        labelIt: 'Corsa',
        label: 'Running',
        colorClass: 'bg-orange-500',
        shadowClass: 'shadow-orange-500/20',
        icon: 'fa-person-running',
    },
    {
        sport: 'swimming',
        labelIt: 'Nuoto',
        label: 'Swimming',
        colorClass: 'bg-cyan-500',
        shadowClass: 'shadow-cyan-500/20',
        icon: 'fa-person-swimming',
    },
    {
        sport: 'cycling',
        labelIt: 'Ciclismo',
        label: 'Cycling',
        colorClass: 'bg-yellow-500',
        shadowClass: 'shadow-yellow-500/20',
        icon: 'fa-person-biking',
    },
];

// ─── Modal state type ─────────────────────────────────────────────────────────

interface ModalState {
    open: boolean;
    sport: SportDiscipline;
    entryType: 'record' | 'test';
    /** Set when editing an existing entry */
    initialValues?: RecordInitialValues | TestInitialValues;
    editId?: string;
}

const MODAL_CLOSED: ModalState = { open: false, sport: 'running', entryType: 'record' };

// ─── Page ─────────────────────────────────────────────────────────────────────

const PersonalTestPage: React.FC = () => {
    const { t } = useTranslation();
    // ── Server state ──────────────────────────────────────────────────────────
    const recordsQuery = usePersonalRecords();
    const testsQuery = usePerformanceTests();

    const createRecord = useCreateRecord();
    const updateRecord = useUpdateRecord();
    const deleteRecord = useDeleteRecord();
    const createTest = useCreateTest();
    const updateTest = useUpdateTest();
    const deleteTest = useDeleteTest();

    // ── Local UI state ────────────────────────────────────────────────────────
    const [modal, setModal] = useState<ModalState>(MODAL_CLOSED);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const showSnack = (message: string, type: 'success' | 'error' = 'success') =>
        setSnackbar({ message, type });

    // ── Modal submit (create or edit) ─────────────────────────────────────────
    const handleModalSubmit = async (payload: EntryPayload) => {
        const editId = modal.editId;
        if (payload.type === 'record') {
            if (editId) {
                await updateRecord.mutateAsync({
                    id: editId,
                    input: {
                        distance: payload.distance,
                        timeValue: payload.timeValue,
                        achievedAt: payload.achievedAt,
                        notes: payload.notes || undefined,
                    },
                });
                showSnack(t('personalTest.recordUpdated'));
            } else {
                await createRecord.mutateAsync({
                    sport: payload.sport,
                    distance: payload.distance,
                    timeValue: payload.timeValue,
                    achievedAt: payload.achievedAt,
                    notes: payload.notes || undefined,
                });
                showSnack(t('personalTest.recordAdded'));
            }
        } else {
            if (editId) {
                await updateTest.mutateAsync({
                    id: editId,
                    input: {
                        testName: payload.testName,
                        result: payload.result,
                        performedAt: payload.performedAt,
                        notes: payload.notes || undefined,
                    },
                });
                showSnack(t('personalTest.testUpdated'));
            } else {
                await createTest.mutateAsync({
                    sport: payload.sport,
                    testName: payload.testName,
                    result: payload.result,
                    performedAt: payload.performedAt,
                    notes: payload.notes || undefined,
                });
                showSnack(t('personalTest.testAdded'));
            }
        }
    };

    // ── Edit handlers ─────────────────────────────────────────────────────────
    const handleEditRecord = (rec: import('./api').PersonalRecord) => {
        setModal({
            open: true,
            sport: rec.sport,
            entryType: 'record',
            editId: rec.id,
            initialValues: {
                id: rec.id,
                distance: rec.distance,
                timeValue: rec.timeValue,
                achievedAt: rec.achievedAt,
                notes: rec.notes ?? '',
            } as RecordInitialValues,
        });
    };

    const handleEditTest = (tst: import('./api').PerformanceTest) => {
        setModal({
            open: true,
            sport: tst.sport,
            entryType: 'test',
            editId: tst.id,
            initialValues: {
                id: tst.id,
                testName: tst.testName,
                result: tst.result,
                performedAt: tst.performedAt,
                notes: tst.notes ?? '',
            } as TestInitialValues,
        });
    };

    // ── Delete handlers ───────────────────────────────────────────────────────
    const handleDeleteRecord = async (id: string) => {
        setDeletingId(id);
        try {
            await deleteRecord.mutateAsync(id);
            showSnack(t('personalTest.entryDeleted'));
        } catch {
            showSnack(t('personalTest.deleteError'), 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const handleDeleteTest = async (id: string) => {
        setDeletingId(id);
        try {
            await deleteTest.mutateAsync(id);
            showSnack(t('personalTest.entryDeleted'));
        } catch {
            showSnack(t('personalTest.deleteError'), 'error');
        } finally {
            setDeletingId(null);
        }
    };

    // ── Client-side filter helpers ────────────────────────────────────────────
    const allRecords: PersonalRecord[] = recordsQuery.data ?? [];
    const allTests: PerformanceTest[] = testsQuery.data ?? [];
    const recordsFor = (sport: SportDiscipline) =>
        allRecords.filter((r) => r.sport === sport);
    const testsFor = (sport: SportDiscipline) =>
        allTests.filter((t) => t.sport === sport);

    const isLoading = recordsQuery.isLoading || testsQuery.isLoading;
    const isError = recordsQuery.isError || testsQuery.isError;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col min-h-screen">
            <PageHeader
                icon="fa-solid fa-stopwatch"
                title={t('personalTest.title')}
                subtitle={t('personalTest.subtitle')}
            />

            <div className="flex-1 overflow-y-auto p-4 lg:p-8">
                <div className="max-w-7xl mx-auto flex flex-col gap-8 pb-20">

                    {isLoading && (
                        <div className="flex items-center justify-center py-24 text-slate-400 gap-3">
                            <i className="fa-solid fa-spinner fa-spin text-2xl"></i>
                            <span className="text-lg">{t('common.loading')}</span>
                        </div>
                    )}

                    {isError && (
                        <div className="flex items-center justify-center py-24 gap-3 text-red-400">
                            <i className="fa-solid fa-circle-exclamation text-2xl"></i>
                            <span>{t('personalTest.loadingError')}</span>
                        </div>
                    )}

                    {!isLoading && !isError && DISCIPLINES.map((d) => (
                        <DisciplineSection
                            key={d.sport}
                            config={d}
                            records={recordsFor(d.sport)}
                            tests={testsFor(d.sport)}
                            deletingId={deletingId}
                            onAddRecord={() => setModal({ open: true, sport: d.sport, entryType: 'record' })}
                            onAddTest={() => setModal({ open: true, sport: d.sport, entryType: 'test' })}
                            onEditRecord={handleEditRecord}
                            onEditTest={handleEditTest}
                            onDeleteRecord={handleDeleteRecord}
                            onDeleteTest={handleDeleteTest}
                        />
                    ))}
                </div>
            </div>

            {modal.open && (
                <AddEntryModal
                    sport={modal.sport}
                    entryType={modal.entryType}
                    initialValues={modal.initialValues}
                    onSubmit={handleModalSubmit}
                    onClose={() => setModal(MODAL_CLOSED)}
                />
            )}

            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}
        </div>
    );
};

export default PersonalTestPage;
